<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('deleteScoreBySession.createTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('deleteScoreBySession.pageTitle'), href: '/student-score-delete-by-session' },
          { text: $t('deleteScoreBySession.createTitle'), active: true }
        ]"
      />
    </div>

    <ts-card>
      <ts-loading-banner :loading="loading || classLoading">
        <ts-card-wrapper>
          <div class="d-flex justify-content-between align-items-start ts-mb-4">
            <div>
              <div class="ts-text-lg ts-font-semibold">
                {{ $t('deleteScoreBySession.selectionTitle') }}
              </div>
              <div class="ts-text-sm ts-text-gray-600">
                {{ $t('deleteScoreBySession.selectionDescription') }}
              </div>
            </div>
            <div class="ts-space-x-2 create-actions">
              <ts-button outline @click.prevent="$router.push({ name: 'student-score-delete-by-session' })">
                {{ $t('back') }}
              </ts-button>
              <ts-button outline color="danger" @click.prevent="resetForm">
                {{ $t('clear') }}
              </ts-button>
            </div>
          </div>

          <div class="row ts-mb-4">
            <div class="col-md-4">
              <div class="selection-step-card" :class="{ active: true }">
                <div class="selection-step-card__step">1</div>
                <div>
                  <div class="selection-step-card__title">
                    {{ $t('deleteScoreBySession.stepChooseStudent') }}
                  </div>
                  <div class="selection-step-card__text">
                    {{ $t('deleteScoreBySession.studentHint') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="selection-step-card" :class="{ active: !!studentId }">
                <div class="selection-step-card__step">2</div>
                <div>
                  <div class="selection-step-card__title">
                    {{ $t('deleteScoreBySession.stepChooseStatusAndClass') }}
                  </div>
                  <div class="selection-step-card__text">
                    {{ $t('deleteScoreBySession.academicStatusHint') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="selection-step-card" :class="{ active: canSearch }">
                <div class="selection-step-card__step">3</div>
                <div>
                  <div class="selection-step-card__title">
                    {{ $t('deleteScoreBySession.stepReviewAndDelete') }}
                  </div>
                  <div class="selection-step-card__text">
                    {{ $t('deleteScoreBySession.stepReviewAndDeleteDescription') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="filter-grid">
            <div class="filter-control">
              <label class="required">{{ $t('deleteScoreBySession.student') }}</label>
              <a-select
                v-model="studentId"
                class="full-width-control"
                showSearch
                allowClear
                :filterOption="false"
                :options="studentOptions"
                :placeholder="$t('deleteScoreBySession.selectStudent')"
                @search="onStudentSearch"
              />
              <small class="text-muted">
                {{ $t('deleteScoreBySession.studentHint') }}
              </small>
            </div>
            <div class="filter-control">
              <label class="required">{{ $t('deleteScoreBySession.academicStatus') }}</label>
              <a-select
                v-model="academicStatusId"
                class="full-width-control"
                allowClear
                :options="academicStatusOptions"
                :placeholder="$t('deleteScoreBySession.selectAcademicStatus')"
              />
              <small class="text-muted">
                {{ $t('deleteScoreBySession.academicStatusHint') }}
              </small>
            </div>
            <div class="filter-control">
              <label class="required">{{ $t('deleteScoreBySession.classroom') }}</label>
              <a-select
                v-model="classId"
                class="full-width-control"
                allowClear
                showSearch
                :options="classOptions"
                :placeholder="$t('deleteScoreBySession.selectClass')"
                :disabled="!studentId || !academicStatusId"
                :loading="classLoading"
                optionFilterProp="label"
              />
              <small class="text-muted">
                {{ classHint }}
              </small>
            </div>
            <div class="filter-control filter-control--action">
              <label>{{ $t('deleteScoreBySession.action') }}</label>
              <div class="filter-action-wrap">
                <a-button
                  class="filter-action-button"
                  type="primary"
                  :disabled="!canSearch"
                  @click.prevent="fetchData"
                >
                  {{ $t('deleteScoreBySession.checkRecords') }}
                </a-button>
              </div>
              <small class="text-muted">
                {{ $t('deleteScoreBySession.actionHint') }}
              </small>
            </div>
          </div>

          <div v-if="selectedSummary.length" class="selection-summary ts-mt-4">
            <div class="selection-summary__header">
              {{ $t('deleteScoreBySession.selectedContext') }}
            </div>
            <div class="selection-summary__items">
              <div
                v-for="(item, index) in selectedSummary"
                :key="index"
                class="selection-summary__item"
              >
                {{ item }}
              </div>
            </div>
          </div>
        </ts-card-wrapper>

        <ts-card-wrapper class="ts-pt-0">
          <div class="d-flex justify-content-between align-items-center ts-mb-3">
            <div>
              <div class="ts-text-lg ts-font-semibold">
                {{ $t('deleteScoreBySession.reviewTitle') }}
              </div>
              <div class="ts-text-sm ts-text-gray-600">
                {{ reviewDescription }}
              </div>
            </div>
            <div v-if="hasSearched && resources.length" class="record-count-pill">
              {{ resources.length }} {{ $t('deleteScoreBySession.recordLabel') }}
            </div>
          </div>

          <a-alert
            v-if="hasSearched && resources.length"
            type="warning"
            show-icon
            class="ts-mb-4"
            :message="$t('deleteScoreBySession.warningTitle')"
            :description="$t('deleteScoreBySession.warningDescription')"
          />

          <div v-if="!hasSearched" class="create-empty-state">
            <div class="create-empty-state__title">
              {{ $t('deleteScoreBySession.startBySelectingFilters') }}
            </div>
            <div class="create-empty-state__description">
              {{ $t('deleteScoreBySession.startBySelectingFiltersDescription') }}
            </div>
          </div>

          <template v-else>
            <div v-if="resources.length" class="overflow-auto">
              <ts-table :columns="columns" :records="resources" :loading="loading">
                <template v-slot="{ record }">
                  <td>{{ record.student_name }}</td>
                  <td>{{ record.class_name }}</td>
                  <td>{{ record.session_name }}</td>
                  <td class="text-center">{{ record.score_entries }}</td>
                  <td class="text-center">{{ record.component_entries }}</td>
                  <td class="text-center">
                    <span
                      class="label"
                      :class="record.has_submission_date ? 'label-warning' : 'label-success'"
                    >
                      {{ record.has_submission_date ? $t('yes') : $t('no') }}
                    </span>
                  </td>
                  <td class="text-center">
                    <a-popconfirm
                      v-if="canDelete"
                      placement="leftTop"
                      :title="$t('deleteScoreBySession.deleteConfirmTitle')"
                      :ok-text="$t('yes')"
                      :cancel-text="$t('no')"
                      :okButtonProps="{ props: { loading: deletingSessionId === record.session_id } }"
                      @confirm="deleteRow(record)"
                    >
                      <a
                        href="#"
                        class="text-danger delete-action-link"
                        :class="{ disabled: deletingSessionId === record.session_id }"
                        @click.prevent
                      >
                        <i
                          class="fas"
                          :class="deletingSessionId === record.session_id ? 'fa-circle-notch spin' : 'fa-trash'"
                        ></i>
                      </a>
                    </a-popconfirm>
                    <span v-else>-</span>
                  </td>
                </template>
              </ts-table>
            </div>

            <div
              v-else
              class="create-empty-state"
            >
              <div class="create-empty-state__title">
                {{ $t('deleteScoreBySession.noRecordsTitle') }}
              </div>
              <div class="create-empty-state__description">
                {{ $t('deleteScoreBySession.noRecordsDescription') }}
              </div>
            </div>
          </template>
        </ts-card-wrapper>
      </ts-loading-banner>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'studentScoreDeleteBySessionCreate',
  data() {
    return {
      loading: false,
      classLoading: false,
      hasSearched: false,
      deletingSessionId: null
    }
  },
  computed: {
    ...mapState('studentScoreDeleteBySession', [
      'resources',
      'students',
      'academic_statuses',
      'classes'
    ]),
    canDelete() {
      return this.$store.getters.authPermissionAllow(
        'delete-student-score-delete-by-sessions'
      )
    },
    canSearch() {
      return !!this.studentId && !!this.academicStatusId && !!this.classId
    },
    studentId: {
      get() {
        return this.$store.state.studentScoreDeleteBySession.student_id
      },
      set(value) {
        this.$store.commit('studentScoreDeleteBySession/SET_STUDENT_ID', value)
      }
    },
    academicStatusId: {
      get() {
        return this.$store.state.studentScoreDeleteBySession.ac_status_id
      },
      set(value) {
        this.$store.commit('studentScoreDeleteBySession/SET_AC_STATUS_ID', value)
      }
    },
    classId: {
      get() {
        return this.$store.state.studentScoreDeleteBySession.class_id
      },
      set(value) {
        this.$store.commit('studentScoreDeleteBySession/SET_CLASS_ID', value)
      }
    },
    studentOptions() {
      return this.students.map(student => ({
        value: student.student_id,
        label: `${student.first_name_en} ${student.last_name_en} (${student.card_number || student.ext_card_number || student.student_id})`
      }))
    },
    academicStatusOptions() {
      return this.academic_statuses.map(status => ({
        value: status.ac_status_id,
        label: status.ac_status_description
      }))
    },
    classOptions() {
      return this.classes.map(item => ({
        value: item.class_id,
        label: [item.class_name, item.grade_name_foreign, item.term_name, item.ac_year]
          .filter(Boolean)
          .join(' - ')
      }))
    },
    selectedStudent() {
      return this.students.find(student => student.student_id === this.studentId)
    },
    selectedAcademicStatus() {
      return this.academic_statuses.find(
        status => status.ac_status_id === this.academicStatusId
      )
    },
    selectedClass() {
      return this.classes.find(item => item.class_id === this.classId)
    },
    selectedSummary() {
      return [
        this.selectedStudent
          ? `${this.$t('deleteScoreBySession.student')}: ${this.selectedStudent.first_name_en} ${this.selectedStudent.last_name_en}`
          : null,
        this.selectedAcademicStatus
          ? `${this.$t('deleteScoreBySession.academicStatus')}: ${this.selectedAcademicStatus.ac_status_description}`
          : null,
        this.selectedClass
          ? `${this.$t('deleteScoreBySession.classroom')}: ${this.selectedClass.class_name}`
          : null
      ].filter(Boolean)
    },
    classHint() {
      if (!this.studentId) return this.$t('deleteScoreBySession.classHintSelectStudent')
      if (!this.academicStatusId) return this.$t('deleteScoreBySession.classHintSelectStatus')
      if (!this.classes.length) return this.$t('deleteScoreBySession.classHintNoClass')
      return this.$t('deleteScoreBySession.classHintReady')
    },
    reviewDescription() {
      if (!this.hasSearched) return this.$t('deleteScoreBySession.reviewDescription')
      if (!this.resources.length) return this.$t('deleteScoreBySession.noRecordsDescription')
      return this.$t('deleteScoreBySession.reviewDescriptionReady')
    },
    columns() {
      return [
        { name: this.$t('deleteScoreBySession.student') },
        { name: this.$t('deleteScoreBySession.classroom') },
        { name: this.$t('deleteScoreBySession.session') },
        { name: this.$t('deleteScoreBySession.scoreEntries'), style: 'text-align:center' },
        { name: this.$t('deleteScoreBySession.componentEntries'), style: 'text-align:center' },
        { name: this.$t('deleteScoreBySession.released'), style: 'text-align:center' },
        { name: this.$t('actions'), style: 'text-align:center' }
      ]
    }
  },
  created() {
    this.fetchAcademicStatuses()
    this.searchStudents()
  },
  methods: {
    searchStudents: debounce(function(search = null) {
      this.$store
        .dispatch('studentScoreDeleteBySession/getStudents', {
          search,
          perPage: 20
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    }, 300),
    onStudentSearch(value) {
      this.searchStudents(value)
    },
    fetchAcademicStatuses() {
      this.$store
        .dispatch('studentScoreDeleteBySession/getAcademicStatuses')
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    fetchClasses() {
      if (!this.studentId || !this.academicStatusId) return

      this.classLoading = true
      this.$store
        .dispatch('studentScoreDeleteBySession/getStudentClasses', {
          student_id: this.studentId,
          ac_status_id: this.academicStatusId
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.classLoading = false
        })
    },
    fetchData() {
      if (!this.studentId || !this.classId) {
        this.$toasted.error(this.$t('deleteScoreBySession.selectStudentAndClass'))
        return
      }

      this.hasSearched = true
      this.loading = true
      this.$store
        .dispatch('studentScoreDeleteBySession/fetchDeleteRecords')
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetForm() {
      this.hasSearched = false
      this.$store.commit('studentScoreDeleteBySession/RESET_STATE')
      this.fetchAcademicStatuses()
      this.searchStudents()
    },
    deleteRow(record) {
      this.deletingSessionId = record.session_id
      this.$store
        .dispatch('studentScoreDeleteBySession/destroy', {
          student_id: record.student_id,
          class_id: record.class_id,
          session_id: record.session_id
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'student-score-delete-by-session' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.deletingSessionId = null
        })
    }
  },
  watch: {
    studentId() {
      this.hasSearched = false
      this.$store.commit('studentScoreDeleteBySession/SET_CLASS_ID', null)
      this.$store.commit('studentScoreDeleteBySession/SET_CLASSES', [])
      this.$store.commit('studentScoreDeleteBySession/RECEIVE_RESOURCES', { data: [] })
      if (this.studentId && this.academicStatusId) {
        this.fetchClasses()
      }
    },
    academicStatusId() {
      this.hasSearched = false
      this.$store.commit('studentScoreDeleteBySession/SET_CLASS_ID', null)
      this.$store.commit('studentScoreDeleteBySession/SET_CLASSES', [])
      this.$store.commit('studentScoreDeleteBySession/RECEIVE_RESOURCES', { data: [] })
      if (this.studentId && this.academicStatusId) {
        this.fetchClasses()
      }
    },
    classId() {
      this.hasSearched = false
      this.$store.commit('studentScoreDeleteBySession/RECEIVE_RESOURCES', { data: [] })
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('studentScoreDeleteBySession/RESET_STATE')
    next()
  }
}
</script>

<style scoped>
.create-actions {
  display: flex;
  gap: 8px;
}

.full-width-control {
  width: 100%;
}

.full-width-control /deep/ .ant-select {
  width: 100%;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(180px, 1fr);
  gap: 16px 18px;
  align-items: start;
}

.filter-control {
  display: flex;
  flex-direction: column;
}

.filter-control label {
  display: block;
  margin-bottom: 6px;
}

.filter-control small {
  display: block;
  margin-top: 6px;
}

.filter-action-wrap {
  width: 100%;
  display: flex;
  align-items: center;
}

.filter-control--action {
  justify-content: flex-start;
}

.delete-action-link.disabled {
  pointer-events: none;
  opacity: 0.65;
}

.filter-action-button {
  width: 100%;
  height: 32px;
  line-height: 30px;
  padding-top: 0;
  padding-bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.selection-step-card {
  display: flex;
  gap: 12px;
  min-height: 88px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
}

.selection-step-card.active {
  border-color: #91d5ff;
  background: #f0f9ff;
}

.selection-step-card__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 600;
  flex-shrink: 0;
}

.selection-step-card__title {
  font-weight: 600;
  color: #111827;
}

.selection-step-card__text {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.selection-summary {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.selection-summary__header {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.selection-summary__items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selection-summary__item {
  padding: 6px 10px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0c4a6e;
  font-size: 12px;
}

.record-count-pill {
  padding: 6px 12px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}

.create-empty-state {
  padding: 48px 20px;
  text-align: center;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #fcfcfd;
}

.create-empty-state__title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.create-empty-state__description {
  margin-top: 8px;
  color: #6b7280;
}
@media (max-width: 767px) {
  .create-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .selection-step-card {
    margin-bottom: 12px;
  }
}
</style>
